import { observer } from "mobx-react"
import { EMPTY_OBJECT_ID } from "modules/common/constants"
import { routes } from "modules/common/routes"
import { trimStart } from "modules/common/services/strings"
import { OrderTabs } from "modules/orders-manage/details/OrderDetailsTabsPage"
import { OrderExternalParams } from "modules/orders-manage/models/order"
import { parse } from "query-string"
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { useLocation } from "react-router"
import { OrderQuestionStoreType, StoredPortfolioId } from "../models/store"
import { QuestionsBoard } from "./QuestionsBoard"
import styles from "./Tab.module.scss"
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate"

/**
 * Доска заданий: на своей странице показывает все проекты и портфели,
 * во вкладке проекта — только его задания
 */
export const QuestionsTab: React.FC<TabProps> = observer((props) => {
  const {
    store,
    canManage,
    hideProject,
    orderId,
    canAddQuestion,
    currentUserId,
    orderGip,
    questionId,
    embedded,
    compact,
  } = props

  const location = useLocation()
  const navigate = useStableNavigate()

  // ссылка на задания сотрудника: #employerId=<id>. Задание адресуется путём.
  // В виджете главной адрес принадлежит самой странице: доска его не читает и не меняет
  const external = useRef(
    (() => {
      const hash = embedded ? "" : trimStart("#", location.hash)
      const metadata = hash ? parse(hash) : {}

      return {
        employerId: (metadata["employerId"] as string) || "",
      }
    })()
  )

  /** Задание из адреса: своя страница держит его в пути, вкладка договора — в хэше */
  const linkedId = embedded ? "" : orderId ? OrderExternalParams(location.hash).rowId : questionId || ""

  // ссылки из старых писем ведут на /order-questions#rowId=<id>: переводим их на путь,
  // чтобы адрес задания дальше был один и тем же
  const legacyId = useRef(embedded || orderId ? "" : OrderExternalParams(location.hash).rowId)

  useEffect(() => {
    if (legacyId.current && !questionId) {
      navigate(routes.orders.orderQuestionDetails(legacyId.current), { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Открытое задание видно в адресной строке: такой ссылкой можно поделиться */
  const showQuestionUrl = useCallback(
    (id: string) => {
      const path = orderId
        ? routes.orders.orderQuestionDetailsTab(orderId, id)
        : routes.orders.orderQuestionDetails(id)

      if (window.location.pathname + window.location.hash !== path) {
        navigate(path)
      }
    },
    [orderId, navigate]
  )

  const hideQuestionUrl = useCallback(() => {
    const path = orderId ? routes.orders.orderQuestionsTab(orderId) : routes.orders.orderQuestions

    // расширенная страница колонки нужна была только ради карточки из ссылки
    store.setRevealId("")

    if (window.location.pathname + window.location.hash !== path) {
      navigate(path, { replace: true })
    }
  }, [orderId, navigate, store])

  /**
   * Номер последнего запуска загрузки. Индикацию снимает только он: отменённый запуск
   * (StrictMode в dev монтирует эффект дважды, в проде — смена договора во вкладке)
   * иначе выключал бы её посреди загрузки нового — пустые колонки или прежние
   * карточки мелькали, и потом доска грузилась заново.
   */
  const initRun = useRef(0)

  useEffect(() => {
    let cancelled = false
    const run = ++initRun.current
    const stopLoading = () => run === initRun.current && store.setBoardLoading(false)

    const init = async () => {
      store.setBoardLoading(true)

      // контекст договора ставим до загрузки доски: по нему сервер отберёт личные доски,
      // где у пользователя есть задание по этому договору
      store.setOrderContext(orderId)
      store.initFilters()

      // задание из ссылки грузим вместе с доской: его колонка приходит сразу до карточки
      const reveal = legacyId.current || linkedId
      store.setRevealId(reveal)

      await store.loadBoard()

      if (cancelled) {
        stopLoading()
        return
      }

      // задание из ссылки может лежать на другой доске — открываем ту, где оно есть
      const linked = !hideProject && reveal && store.portfoliosEnabled ? await store.questionPortfolio(reveal) : null

      if (cancelled) {
        stopLoading()
        return
      }

      // на своей странице возвращаемся к портфелю, открытому в прошлый раз. Задания сотрудника
      // открываем на общей доске: по личным он переключится сам, фильтр по нему останется
      const stored = external.current.employerId ? "" : StoredPortfolioId()
      const restore =
        !hideProject && stored && store.portfoliosEnabled && store.portfolios.some((p) => p.id === stored)

      if (linked !== null && (linked === "" || store.portfolios.some((p) => p.id === linked))) {
        // доска задания важнее запомненной: иначе переход открывает пустую доску
        if (linked !== store.portfolioId) {
          await store.selectPortfolio(linked)
        }
      } else if (restore) {
        await store.selectPortfolio(stored)
      }

      if (cancelled) {
        stopLoading()
        return
      }

      await store.setOrderId(orderId, external.current.employerId, true)

      store.setBoardLoading(false)
    }

    init().catch(stopLoading)

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  const showAll = useCallback(() => {
    external.current = { employerId: "" }

    const path = orderId
      ? routes.orders.details(orderId, OrderTabs.orderQuestion)
      : routes.orders.orderQuestions + "#c"

    window.open(path, "_self")
    store.setOrderId(orderId)
  }, [orderId, store])

  const hasExternalFilter = useMemo(() => !!external.current.employerId, [])

  if (orderId === EMPTY_OBJECT_ID) {
    return <h2>Сначала необходимо сохранить основные данные договора</h2>
  }

  return (
    <div className={styles.tab}>
      <QuestionsBoard
        store={store}
        canManage={canManage}
        canAddQuestion={canAddQuestion}
        hideProject={hideProject}
        currentUserId={currentUserId}
        orderGip={orderGip}
        compact={compact}
        linkedQuestionId={linkedId}
        onQuestionOpened={embedded ? undefined : showQuestionUrl}
        onQuestionClosed={embedded ? undefined : hideQuestionUrl}
        onShowAll={hasExternalFilter ? showAll : undefined}
      />
    </div>
  )
})

interface TabProps {
  canManage: boolean
  store: OrderQuestionStoreType
  hideProject?: boolean
  canClientEdit?: boolean
  orderId?: string
  isActiveTab: boolean
  canAddQuestion?: boolean
  currentUserId: string
  orderGip?: string | null
  /** Задание из пути /order-questions/<id>: открывается сразу */
  questionId?: string
  /**
   * Доска внутри чужой страницы (виджет главной): адресную строку она не трогает,
   * задание открывается диалогом поверх
   */
  embedded?: boolean
  /** Узкая доска виджета: колонки уже, карточки плотнее, фильтры свёрнуты */
  compact?: boolean
}
